var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { Button, MessageBanner, TextArea } from '@folio/stripes/components';
import { replyToReturn } from '../../api/proposalService';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getErrorKind } from '../../utils/errorKind';
import { PROPOSALS_QUERY_ROOT } from '../../hooks/useProposalList';
import useUserName from '../../hooks/useUserName';
/** Same limit as the backend's {@code ReviewReplyRequest}. */
var MAX_REPLY_LENGTH = 2000;
var recordUrl = function (id) { return "/czna-authorities/authorities/".concat(id); };
var ThreadItem = function (_a) {
    var _b, _c;
    var item = _a.item;
    var intl = useIntl();
    var name = useUserName(item.authorId).name;
    var when = item.createdAt
        ? "".concat(intl.formatDate(item.createdAt, { day: '2-digit', month: '2-digit', year: 'numeric' }), " ").concat(intl.formatTime(item.createdAt, { hour: '2-digit', minute: '2-digit' }))
        : null;
    // CZNA-1525 — the server's own message (e.g. "záznam byl zrušen"): nobody wrote it, so it
    // is signed by the system rather than by an unknown author with a role.
    var authorRole = item.authorRole;
    var author = authorRole === 'SYSTEM' ? (React.createElement("strong", null,
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.system" }))) : (React.createElement(React.Fragment, null,
        React.createElement("strong", null, name !== null && name !== void 0 ? name : React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.unknownAuthor" })),
        ' · ',
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.role.".concat(authorRole) })));
    return (React.createElement("li", { "data-testid": "review-thread-item", "data-author-role": authorRole, style: { marginBottom: '0.75em' } },
        React.createElement("div", null,
            author,
            when && React.createElement(React.Fragment, null,
                ' · ',
                React.createElement("span", { "data-testid": "review-thread-item-time" }, when))),
        React.createElement("div", { style: { whiteSpace: 'pre-wrap' } }, item.body),
        (item.targetRecordId || item.successorRecordId) && (React.createElement("div", { "data-testid": "review-thread-item-records" },
            item.targetRecordId && (React.createElement("span", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.targetRecord" }),
                ': ',
                React.createElement(Link, { to: recordUrl(item.targetRecordId) }, (_b = item.targetRecordAuthorityId) !== null && _b !== void 0 ? _b : item.targetRecordId))),
            item.targetRecordId && item.successorRecordId && ' · ',
            item.successorRecordId && (React.createElement("span", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.successorRecord" }),
                ': ',
                React.createElement(Link, { to: recordUrl(item.successorRecordId) }, (_c = item.successorRecordAuthorityId) !== null && _c !== void 0 ? _c : item.successorRecordId)))))));
};
/**
 * CZNA-949 — the review conversation on a returned proposal or record: every supervisor
 * comment and every author answer, oldest first, each with who and when. Before it, the
 * single `reviewComment` column was overwritten by the next action and the author had no
 * way to answer at all.
 */
var ReviewThread = function (_a) {
    var items = _a.items, replyToProposalId = _a.replyToProposalId;
    var intl = useIntl();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useState(''), body = _b[0], setBody = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    // The detail pane is reused when the list selection changes: an answer typed for one
    // proposal must not stay in the box, ready to be sent to the next one.
    useEffect(function () {
        setBody('');
        setError(null);
    }, [replyToProposalId]);
    var replyMutation = useMutation(function () { return replyToReturn(ky, replyToProposalId, body.trim()); }, {
        onSuccess: function () {
            setBody('');
            setError(null);
            queryClient.invalidateQueries(['proposal', replyToProposalId]);
            queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
        },
        onError: function (e) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(e)];
                    case 1:
                        info = _a.sent();
                        // CZNA-1625 — a reply nobody answered may have been posted: check before sending it again.
                        if (getErrorKind(info) === 'timeout') {
                            queryClient.invalidateQueries(['proposal', replyToProposalId]);
                            setError(intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }));
                            return [2 /*return*/];
                        }
                        setError(intl.formatMessage({
                            id: info.status === 403
                                ? 'ui-czna-authorities.proposals.detail.forbidden'
                                : 'ui-czna-authorities.reviewThread.reply.error',
                        }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    return (React.createElement("div", { "data-testid": "review-thread" },
        React.createElement("ol", { style: { listStyle: 'none', paddingLeft: 0, margin: 0 } }, items.map(function (item, index) { return (
        // Items have no id of their own on the wire; the order is fixed and append-only.
        // eslint-disable-next-line react/no-array-index-key
        React.createElement(ThreadItem, { key: index, item: item })); })),
        replyToProposalId && (React.createElement("div", { "data-testid": "review-thread-reply" },
            React.createElement(TextArea, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.reply.label" }), value: body, maxLength: MAX_REPLY_LENGTH, onChange: function (e) { return setBody(e.target.value); } }),
            error && React.createElement(MessageBanner, { type: "error" }, error),
            React.createElement(Button, { buttonStyle: "primary", disabled: !body.trim() || replyMutation.isLoading, onClick: function () { return replyMutation.mutate(); }, "data-testid": "review-thread-reply-button" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.reviewThread.reply.submit" }))))));
};
export default ReviewThread;
